import * as React from "react";
import {
  CellContext,
  createColumnHelper
} from "@tanstack/react-table";
import styled from "styled-components";

import { Text } from "src/library/components/Text";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { formatPrice } from "src/core/prices/utils/price-helpers";
import {
  Invoice,
  InvoiceStatus
} from "src/core/accounting/api/accounting.api";
import AccountingStatusBadge from "src/core/accounting/components/AccountingStatusBadge";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";

const InvoiceNumberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export interface IInvoiceTableColumns {
  actionButton?: (info: CellContext<Invoice, unknown>) => React.ReactNode;
  showActions?: boolean;
  /** Compact mode for dashboard widget (fewer columns) */
  compact?: boolean;
  /** Terminology for dynamic column headers */
  terminology?: ITerminology;
  /** Show matched value column (for transaction-invoice relationship) */
  showMatchedValue?: boolean;
}

export const invoiceTableColumns = (params: IInvoiceTableColumns) => {
  const customColumnHelper = customCreateColumnHelper<Invoice>();
  const columnHelper = createColumnHelper<Invoice>();

  // Invoice # column with document icon (matches Figma design)
  const invoiceNumberColumn = customColumnHelper.standardTextColumn(columnHelper, "invoiceNumber", {
    header: "Invoice #",
    cell: ({ getValue }) => {
      const invoiceNumber = getValue() as string;

      return (
        <InvoiceNumberCell>
          <Text>
            {`Invoice # ${invoiceNumber}`}
          </Text>
        </InvoiceNumberCell>
      );
    }
  });

  // Payment Due date column
  const paymentDueColumn = customColumnHelper.dateColumn(columnHelper, "dueDate", {
    header: "Payment Due",
    enableSorting: true
  });

  // Total amount column
  const totalColumn = customColumnHelper.priceColumn(columnHelper, "totalAmount", { header: "Invoice Total" }, "GBP");
  const outstandingBalanceColumn = customColumnHelper.priceColumn(columnHelper, "outstandingAmount", { header: "Outstanding Amount" }, "GBP");

  // Status badge column
  const statusColumn = customColumnHelper.standardTextColumn(columnHelper, "status", {
    header: "Status",
    cell: ({ getValue }) => {
      const status = getValue() as InvoiceStatus;

      return (
        <AccountingStatusBadge
          variant="invoice"
          status={status}
          compact
        />
      );
    }
  });

  const dropColumn = customColumnHelper.standardTextColumn(columnHelper, "branch", {
    header: params.terminology?.drop.singular.text,
    cell: ({ row }) => (

      <Text>
        {row.original.branch ? formatDropName(row.original.branch) : "-"}
      </Text>
    ),
    enableSorting: false
  });

  // Build columns array
  const columns = [
    invoiceNumberColumn,
    dropColumn,
    paymentDueColumn,
    totalColumn,
    outstandingBalanceColumn,
    statusColumn
  ];

  // Add matched value column if enabled (for transaction-invoice relationship)
  // Display as positive value for intuitive user experience
  if (params.showMatchedValue) {
    const matchedValueColumn = customColumnHelper.priceColumn(columnHelper, "matchedValue", {
      id: "matchedValue",
      header: "Value",
      cell: ({ row }) => {
        const matchedValue = row.original.matchedValue;

        if (matchedValue === null || matchedValue === undefined) {
          return (
            <Text>
              -
            </Text>
          );
        }

        // Display as positive value for intuitive UX
        const displayValue = Math.abs(matchedValue);

        return (
          <Text>
            {formatPrice(displayValue)}
          </Text>
        );
      }
    }, "GBP");

    columns.push(matchedValueColumn);
  }

  // Add actions column if enabled
  if (params.showActions && params.actionButton) {
    columns.push(customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 150 }));
  }

  return columns;
};
